// TODO: keyboard nav (arrow keys for next/prev issue) — requires onNext/onPrev props from parent
import { useState } from "react";
import { openWithCacheBuster } from "./cacheBuster";
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  ExternalLink,
  Image as ImageIcon,
  LifeBuoy,
  Loader2,
  MessageCircle,
  Maximize2,
  Minimize2,
  RefreshCw,
  RotateCcw,
  Sparkles,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FixingLoader, FixingOverlay } from "@/components/ui/FixingLoader";
import { SeverityBadge } from "./SeverityBadge";
import { StatusBadge } from "./StatusBadge";
import {
  calculateSeverity,
  getLargeImageResultState,
  getIssueTypeLabel,
} from "@/types/technicalAudit";
import { TextDiffCard } from "./TextDiffCard";
import { H1CandidatePicker } from "./H1CandidatePicker";
import { SeoMetaEditCard } from "./SeoMetaEditCard";
import { ImageAltDiffCard } from "./ImageAltDiffCard";
import { RedirectChainCard } from "./RedirectChainCard";
import { BrokenLinkCard } from "./BrokenLinkCard";
import { CanonicalCard } from "./CanonicalCard";
import { DuplicateTitleComparison } from "./DuplicateTitleComparison";
import { DuplicateMetaComparison } from "./DuplicateMetaComparison";
import { ThinContentCard } from "./ThinContentCard";
import { readParagraphDiffs } from "./thinContentParagraphs";
import { IndexabilityCard } from "./IndexabilityCard";
import { NoindexImportantCard } from "./NoindexImportantCard";
import { ResolverSuggestionCard } from "./ResolverSuggestionCard";
import { SitemapErrorCard } from "./SitemapErrorCard";
import { MissingCompressionCard } from "./MissingCompressionCard";
import { SlowResponseCard } from "./SlowResponseCard";
import { NearDuplicateCard } from "./NearDuplicateCard";
import { LowReadabilityDiffCard } from "./LowReadabilityDiffCard";
import { LowTextRatioCard } from "./LowTextRatioCard";
import { StaleContentCard } from "./StaleContentCard";
import { OrphanPageCard } from "./OrphanPageCard";
import { BrokenExternalCard } from "./BrokenExternalCard";
import { LinksToBrokenCard } from "./LinksToBrokenCard";
import { LinksToRedirectCard } from "./LinksToRedirectCard";
import { isAutoSuggestFailed } from "./IssueTable";
import { DeepPageCard } from "./DeepPageCard";
import { UnsafeCrossoriginCard } from "./UnsafeCrossoriginCard";
import { NofollowInternalCard } from "./NofollowInternalCard";
import { LowInternalLinksCard } from "./LowInternalLinksCard";
import { InternalLinkSuggestionCard } from "./InternalLinkSuggestionCard";
import { TitleTooShortCard } from "./TitleTooShortCard";
import { SerpTitleTruncatedCard } from "./SerpTitleTruncatedCard";
import { SerpMetaTruncatedCard } from "./SerpMetaTruncatedCard";
import { FixHistoryTimeline } from "./FixHistoryTimeline";
import { FixHistoryPanel } from "./FixHistoryPanel";
import { ConfidenceBadge } from "./ConfidenceBadge";
import { MultipleMetaCard } from "./MultipleMetaCard";
import { DuplicateH1Card } from "./DuplicateH1Card";
import { DuplicateH1EditCard } from "./DuplicateH1EditCard";
import { LargeImageCard } from "./LargeImageCard";
import { BrokenImageCard } from "./BrokenImageCard";
import { NonNextgenImageCard } from "./NonNextgenImageCard";
import { LargePageCard } from "./LargePageCard";
import { RedirectLoopCard } from "./RedirectLoopCard";
import { MixedContentCard } from "./MixedContentCard";
import { SchemaErrorCard } from "./SchemaErrorCard";
import { MissingSchemaCard } from "./MissingSchemaCard";
import { HreflangCard } from "./HreflangCard";
import { KeywordCannibalizationCard } from "./KeywordCannibalizationCard";
import { ImageMissingDimensionsCard } from "./ImageMissingDimensionsCard";
import { CoreWebVitalsCard } from "./CoreWebVitalsCard";
import { GenericDiffCard } from "./GenericDiffCard";
import { MissingFromSitemapCard } from "./MissingFromSitemapCard";
import type {
  IssueStatus,
  PageIssue,
  ProposedFix,
  SitemapApplyGate,
} from "@/types/technicalAudit";
import { isMissingH1Issue } from "@/types/technicalAudit";
import { SOURCE_EDIT_ISSUE_TYPES } from "@/types/technicalAudit";
import {
  useSourcePublishState,
  useRecoveryActions,
  useRequestExpertHelp,
  useRequestManualFix,
} from "@/hooks/useTechnicalAuditIssues";
import { useOptionalCentralChatBridge } from "@/layouts/CentralChatBridgeRegister";
import { ExpertHelpDialog } from "./ExpertHelpDialog";
import { shouldShowExpertHelp } from "./expertHelpVisibility";
import { PublishFirstNotice } from "./PublishFirstNotice";
import { getIssueTypeHelp, isDirectApplyIssueType } from "./issueTypeHelp";
import {
  getWordPressEditUrl,
  isOverridableReviewIssue,
  isReviewOnlyIssue,
} from "./reviewOnly";
import {
  getReadabilityDisplayStatus,
  isStaleReadabilitySentenceFix,
} from "./readabilityWorkflow";
import { RecoveryActions } from "./RecoveryActions";

interface IssueDiffDrawerProps {
  issue: PageIssue | null;
  open: boolean;
  onClose: () => void;
  onReject: (issueId: string) => void;
  onApply?: (
    issueId: string,
    editedValue?: ProposedFix,
    // confirm_site_wide (missing_from_sitemap): set ONLY from the deliberate
    // site-wide confirmation checkbox in MissingFromSitemapCard.
    opts?: { force?: boolean; confirm_site_wide?: boolean },
  ) => void;
  /** Optional: open the before/after preview modal for inspection (does NOT
   *  apply). Surfaced for direct-apply issue types (H1) whose primary action
   *  applies straight to WordPress. */
  onPreview?: (
    issueId: string,
    editedValue?: ProposedFix,
    opts?: { confirm_site_wide?: boolean },
  ) => void;
  onVerify?: (issueId: string) => void;
  onGenerateSuggestion?: (issueId: string, force?: boolean) => void;
  isGeneratingSuggestion?: boolean;
  /** True while a single-issue apply is in flight (disables Apply to prevent a
   *  double-send on the direct-apply path). */
  isApplying?: boolean;
}

const AI_SUGGESTION_TYPES = new Set([
  "title_meta",
  "h1",
  "h1_missing",
  "h1_multiple",
  "h1_too_long",
  "alt_text",
  "duplicate_title",
  "page_title_same_as_h1",
  "duplicate_meta",
  "title_too_short",
  "serp_title_truncated",
  "serp_meta_truncated",
  // Atomic split of title_meta — auto title/meta types get an AI suggestion +
  // SEO/meta edit card.
  "page_title_missing",
  "page_title_over_60_chars",
  "page_title_below_30_chars",
  "meta_missing",
  "meta_description_over_155_chars",
  "meta_description_below_70_chars",
  // multiple_meta: the backend picks (or consolidates) the single best
  // description from the page's duplicate meta tags and returns
  // { recommended_meta_description, recommended_source,
  //   current_meta_descriptions }. MultipleMetaCard renders it with pickable
  // keep-#1/keep-#2 options + an editable textarea.
  "multiple_meta",
  // duplicate_h1 gets an AI suggestion ({ new_h1, reasoning }) + editable
  // Proposed-H1 card that rewrites the page's VISIBLE H1 heading (never the
  // SEO/meta title tag).
  "duplicate_h1",
  "orphan_page",
  "low_internal_links",
  "broken_internal",
  "indexability",
  // canonical: backend now exposes a /generate-suggestion path
  // (technical_audit_canonical_resolver) + an apply path (_apply_canonical_fix),
  // mirroring indexability. Cross-domain comes back review_only (overridable).
  "canonical",
  "sitemap_error",
  "links_to_redirect",
  // links_to_broken: source-page-anchored (mirrors broken_internal). The backend
  // technical_audit_links_to_broken_resolver produces a repoint/remove edit per
  // broken internal target; the ResolverSuggestionCard renders + edits them.
  "links_to_broken",
  // redirect_loop: backend exposes a review-only /generate-suggestion payload
  // (technical_audit_redirect_loop_resolver, action="review"). There is no safe
  // automatic target, so the suggestion surfaces guidance and the apply path
  // stays review-only.
  "redirect_loop",
  // redirect / redirect_chain: the backend resolver
  // (technical_audit_redirect_chain_resolver) follows the URL's chain LIVE to its
  // final destination and proposes a direct 301 (or review-only when it no longer
  // redirects / is a single hop / is server-CDN-shadowed). Without this the card
  // prompted "Click Generate suggestion" but no button existed and there was no
  // manual input either — a dead end.
  "redirect",
  "redirect_chain",
  "low_readability",
  "thin_content",
  // LLM-first OG tags: the backend's /generate-suggestion returns a structured
  // { og_title, og_description, og_image, og_url, og_type } object in
  // proposed_value. Only the OG fields listed in issue_details.missing are
  // rendered (MissingOgTagsDiffCard).
  "missing_og_tags",
  // missing_schema gets an AI suggestion ({ schema_jsonld, recommended_schema_type,
  // reasoning, ... }) + an editable JSON-LD card (MissingSchemaCard). Apply-ability
  // is governed by the fix payload's apply_disabled (Article/FAQ apply; entity types
  // apply via JSON-LD injection when the backend confirms plugin support, else are
  // handed off), not by the blog-vs-page body gate.
  "missing_schema",
  // schema_validation_error gets an AI suggestion ({ schema_jsonld (corrected),
  // error_explanations, validation_errors, ... }) rendered by the same
  // MissingSchemaCard. Apply-ability is governed by the fix payload's apply_disabled
  // (a JSON-LD injection rewrite when the backend proves it is our own injected
  // block + plugin support, else handed off — never fighting Yoast/theme schema).
  "schema_validation_error",
]);

// Feature flag: the editable broken_external fix UI (Generate fix +
// ResolverSuggestionCard) is gated on the matching backend generator/apply flag.
const BROKEN_EXTERNAL_FIX_ENABLED =
  import.meta.env.VITE_TECHNICAL_AUDIT_BROKEN_EXTERNAL_FIX === "true";


// NOTE: broken_external is intentionally NOT an AI-suggestion type. Unlike the
// other resolvers (broken_internal/indexability/sitemap_error/links_to_redirect),
// the backend's /generate-suggestion endpoint REJECTS broken_external — it has its
// own flow (replacement URL / "Suggest replacements" candidates) rendered via
// BROKEN_EXTERNAL_FIX_ENABLED. Adding it here made the "Generate fix" button appear
// and call /generate-suggestion, which 4xx'd ("AI suggestions are not supported for
// issue type: broken_external"), and also gated Apply behind that failing call.

const SEO_META_EDIT_TYPES = new Set([
  "title_meta",
  "duplicate_title",
  "page_title_same_as_h1",
  "duplicate_meta",
  "title_too_short",
  "serp_title_truncated",
  "serp_meta_truncated",
  // Atomic split of title_meta — auto title/meta types render the SEO/meta edit
  // card (title-only types show only the title field, meta-only only the meta
  // field; enforced by SeoMetaEditCard's TITLE_TYPES/META_TYPES sets).
  "page_title_missing",
  "page_title_over_60_chars",
  "page_title_below_30_chars",
  "meta_missing",
  "meta_description_over_155_chars",
  "meta_description_below_70_chars",
]);

const hasMeaningfulFixValue = (value: ProposedFix | null): boolean => {
  if (!value) return false;
  return Object.values(value).some((item) => {
    if (typeof item === "string") return item.trim().length > 0;
    if (Array.isArray(item)) return item.length > 0;
    return Boolean(item);
  });
};

const parseFixObject = (value: unknown): Record<string, unknown> | null => {
  if (!value) return null;
  if (typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
};

// missing_schema apply gate: read apply_disabled from the fix payload (approved
// value, then the generated suggestion; object or JSON string). apply_disabled
// means there is no proven live-write path for this fix (an entity type on a
// site whose plugin lacks JSON-LD injection support, LocalBusiness without an
// address, etc.) so it is handed off for manual implementation and the drawer
// hides the Apply button (review/handoff only). The backend decides capability;
// the frontend only reads this flag.
const isMissingSchemaApplyDisabled = (issue: PageIssue): boolean => {
  const fix =
    parseFixObject(issue.approved_value) ??
    parseFixObject(issue.proposed_fix) ??
    parseFixObject(issue.proposed_value);
  return fix?.apply_disabled === true;
};

const hasInternalLinkSourceSelection = (value: unknown): boolean => {
  const fix = parseFixObject(value);
  if (!fix) return false;
  const sourcePages = Array.isArray(fix.source_pages) ? fix.source_pages : [];
  if (
    sourcePages.some((item) => {
      if (!item || typeof item !== "object" || Array.isArray(item))
        return false;
      const source = (item as Record<string, unknown>).source_page_url;
      const anchor = (item as Record<string, unknown>).anchor_text;
      return (
        typeof source === "string" &&
        source.trim().length > 0 &&
        typeof anchor === "string" &&
        anchor.trim().length > 0
      );
    })
  ) {
    return true;
  }
  return (
    typeof fix.source_page_url === "string" &&
    fix.source_page_url.trim().length > 0 &&
    typeof fix.anchor_text === "string" &&
    fix.anchor_text.trim().length > 0
  );
};

const isManualReviewInternalLinkSource = (
  item: Record<string, unknown>,
  recommendedByUrl: Map<string, Record<string, unknown>>,
): boolean => {
  const source =
    typeof item.source_page_url === "string"
      ? item.source_page_url
      : typeof item.url === "string"
        ? item.url
        : "";
  const recommended = recommendedByUrl.get(source);

  // "Needs anchor selection" describes a MISSING ANCHOR, not a permanently
  // unusable source. Once the OPERATOR has supplied one — picking a suggested
  // phrase or typing an override — the review is done and the row must survive
  // sanitization; stripping it deleted the very source just chosen and left
  // Apply disabled with no explanation. Apply still re-verifies the anchor
  // against the live body before writing.
  //
  // Keyed on the explicit operator-selected marker, NOT on anchor_text being
  // non-empty: the backend also puts a *suggested* anchor on unverified
  // fallback rows, and those must keep being stripped — an unreviewed guess is
  // exactly what this sanitization exists to keep out of an apply.
  if (item.anchor_selection_status === "operator_selected") {
    return false;
  }

  return Boolean(
    item.manual_review_required ||
    item.needs_anchor_selection ||
    item.anchor_selection_status === "needs_anchor_selection" ||
    item.has_anchor === false ||
    item.source === "ranked_fallback" ||
    recommended?.manual_review_required ||
    recommended?.needs_anchor_selection ||
    recommended?.anchor_selection_status === "needs_anchor_selection" ||
    recommended?.has_anchor === false ||
    recommended?.source === "ranked_fallback",
  );
};

const sanitizeInternalLinkFixSelection = (
  value: unknown,
): ProposedFix | undefined => {
  const fix = parseFixObject(value);
  if (!fix) return undefined;

  const recommendedByUrl = new Map<string, Record<string, unknown>>();
  if (Array.isArray(fix.recommended_source_pages)) {
    for (const item of fix.recommended_source_pages) {
      if (!item || typeof item !== "object" || Array.isArray(item)) continue;
      const source = (item as Record<string, unknown>).url;
      if (typeof source === "string" && source.trim()) {
        recommendedByUrl.set(source, item as Record<string, unknown>);
      }
    }
  }

  const rawSourcePages = Array.isArray(fix.source_pages)
    ? fix.source_pages
    : [];
  const cleanSourcePages = rawSourcePages.filter((item) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) return false;
    return !isManualReviewInternalLinkSource(
      item as Record<string, unknown>,
      recommendedByUrl,
    );
  });

  const first = cleanSourcePages[0] as Record<string, unknown> | undefined;
  if (cleanSourcePages.length > 0) {
    return {
      ...fix,
      source_pages: cleanSourcePages,
      source_page_url:
        typeof first?.source_page_url === "string"
          ? first.source_page_url
          : undefined,
      anchor_text:
        typeof first?.anchor_text === "string" ? first.anchor_text : undefined,
    } as ProposedFix;
  }

  const topLevelSource =
    typeof fix.source_page_url === "string" ? fix.source_page_url : "";
  const topLevelRecommended = recommendedByUrl.get(topLevelSource);
  if (
    topLevelSource &&
    typeof fix.anchor_text === "string" &&
    !topLevelRecommended?.manual_review_required &&
    !topLevelRecommended?.needs_anchor_selection &&
    topLevelRecommended?.anchor_selection_status !== "needs_anchor_selection" &&
    topLevelRecommended?.has_anchor !== false &&
    topLevelRecommended?.source !== "ranked_fallback"
  ) {
    return fix as ProposedFix;
  }

  return {
    ...fix,
    source_pages: [],
    source_page_url: undefined,
    anchor_text: undefined,
  } as ProposedFix;
};

const reviewedFixValueForApply = (
  issue: PageIssue | null,
  editedValue: ProposedFix | null,
): ProposedFix | undefined => {
  if (
    issue?.issue_type !== "orphan_page" &&
    issue?.issue_type !== "low_internal_links"
  ) {
    return editedValue || undefined;
  }
  if (hasInternalLinkSourceSelection(editedValue)) {
    return sanitizeInternalLinkFixSelection(editedValue);
  }
  for (const value of [
    issue.approved_value,
    issue.proposed_fix,
    issue.proposed_value,
  ]) {
    const parsed = parseFixObject(value);
    const sanitized = sanitizeInternalLinkFixSelection(parsed);
    if (hasInternalLinkSourceSelection(sanitized)) {
      return sanitized;
    }
  }
  return undefined;
};

// Phase 1 thin_content blog-only additive apply: resolve the apply gate inputs
// from the issue (classification + grounded + whether there's appendable draft
// content). Mirrors ThinContentCard's resolver (top-level classification, then
// packet copy, then a caution default of page/CRO-risk so unknown never applies).
function readThinContentApplyState(
  issue: PageIssue,
  /**
   * The live per-section selection from ThinContentCard, when the operator has
   * touched it. The apply gate has to read THIS, not only the stored packet:
   * unticking every section leaves the stored brief full of draft copy, so a
   * stored-only gate keeps Apply enabled for a write the backend then refuses
   * ("No reviewed expansion content to apply"). The button must agree with what
   * is actually being sent.
   */
  editedValue?: ProposedFix | null,
): {
  isBlog: boolean;
  croRisk: boolean;
  grounded: boolean;
  hasApplicableContent: boolean;
  /** The classifier defaulted rather than judged — overridable with a confirm. */
  unconfirmed: boolean;
} {
  const parsePacket = (): Record<string, unknown> | null => {
    const raw =
      issue.proposed_fix ?? issue.approved_value ?? issue.proposed_value;
    if (!raw) return null;
    if (typeof raw === "object" && !Array.isArray(raw)) {
      return raw as Record<string, unknown>;
    }
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed)
          ? (parsed as Record<string, unknown>)
          : null;
      } catch {
        return null;
      }
    }
    return null;
  };
  const packet = parsePacket();
  type Cls = { type?: string; cro_risk?: boolean; source?: string };
  const topClass = (
    issue as unknown as { thin_content_classification?: Cls }
  ).thin_content_classification;
  const packetClass = packet?.thin_content_classification as Cls | undefined;
  const cls =
    (topClass && (topClass.type === "blog" || topClass.type === "page")
      ? topClass
      : undefined) ??
    (packetClass && (packetClass.type === "blog" || packetClass.type === "page")
      ? packetClass
      : undefined);
  // Caution default: unknown -> page + CRO-risk (never apply-eligible).
  const isBlog = cls?.type === "blog";
  const croRisk = cls ? cls.cro_risk === true : true;
  // UNCONFIRMED means the classifier never determined the type and fell back to
  // conversion-page-as-precaution — including the case where no classification
  // reached us at all. Those pages get an explicit "Apply anyway"; a page
  // positively identified as a conversion page does not.
  const unconfirmed = !cls || cls.source === "default";
  const grounded = packet?.grounded !== false; // absent => grounded
  // Prefer the LIVE selection over the stored packet. The card emits only the
  // ACCEPTED paragraph rewrites, so an edited payload is the honest answer to
  // "what will be written"; fall back to the stored packet before the card has
  // emitted. Deselecting every row must disable Apply, or the button offers a
  // write the backend refuses ("No reviewed paragraph edits to apply").
  const edited = (editedValue ?? null) as Record<string, unknown> | null;
  const hasParagraphEdits =
    edited && "paragraph_diffs" in edited
      ? readParagraphDiffs(edited).length > 0
      : readParagraphDiffs(packet).length > 0;
  // Legacy packets (generated before paragraph review) still carry a section
  // brief, and bulk apply can still append one — so they remain applicable.
  const sections = Array.isArray(packet?.recommended_sections)
    ? (packet?.recommended_sections as Array<Record<string, unknown>>)
    : [];
  const hasDraftSection = sections.some(
    (s) => typeof s?.draft === "string" && s.draft.trim().length > 0,
  );
  const additions = Array.isArray(packet?.draft_additions)
    ? (packet?.draft_additions as unknown[])
    : [];
  const hasAddition = additions.some(
    (a) => typeof a === "string" && a.trim().length > 0,
  );
  return {
    isBlog,
    croRisk,
    grounded,
    hasApplicableContent: hasParagraphEdits || hasDraftSection || hasAddition,
    unconfirmed,
  };
}

const getSuggestionVersion = (issue: PageIssue): string => {
  const value =
    issue.approved_value || issue.proposed_fix || issue.proposed_value || "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
};

// Helper function to get human-readable issue title
function getIssueTitle(issue: PageIssue | null | undefined): string {
  if (!issue) return "Issue Details";
  const subtypeLabel = getIssueTypeLabel(issue);
  if (
    (issue.issue_type === "title_meta" && subtypeLabel !== "Title/Meta") ||
    (issue.issue_type === "h1" && subtypeLabel !== "H1")
  ) {
    return subtypeLabel;
  }

  const labels: Record<string, string> = {
    title_meta: "Title & Meta Description",
    h1: "H1 Heading",
    h1_missing: "Missing H1",
    h1_multiple: "Multiple H1 Tags",
    h1_too_long: "H1 Too Long",
    alt_text: "Image Alt Text",
    redirect: "Redirect Chain",
    broken_link: "Broken Link",
    broken_internal: "Broken Internal URL",
    canonical: "Canonical Tag",
    duplicate_title: "Duplicate SEO Title Tag",
    page_title_same_as_h1: "Title Matches H1",
    page_title_missing: "Missing SEO Title",
    page_title_over_60_chars: "SEO Title Too Long",
    page_title_below_30_chars: "SEO Title Too Short",
    duplicate_meta: "Duplicate Meta Description",
    meta_missing: "Missing Meta Description",
    meta_description_over_155_chars: "Meta Description Too Long",
    meta_description_below_70_chars: "Meta Description Too Short",
    thin_content: "Thin Content",
    indexability: "Indexability",
    noindex_important: "Noindex on Important Page",
    slow_response: "Slow Response",
    near_duplicate: "Near-Duplicate Content",
    low_readability: "Low Readability",
    low_text_ratio: "Low Text-to-HTML Ratio",
    stale_content: "Stale Content",
    orphan_page: "Orphan Page",
    broken_external: "Broken External Link",
    deep_page: "Deep Page",
    unsafe_crossorigin: "Unsafe Cross-Origin Link",
    nofollow_internal: "Nofollow Internal Link",
    low_internal_links: "Low Internal Links",
    title_too_short: "Title Too Short",
    serp_title_truncated: "SERP Title Truncated",
    serp_meta_truncated: "SERP Description Truncated",
    multiple_meta: "Multiple Meta Descriptions",
    duplicate_h1: "Duplicate H1",
    large_image: "Oversized Image",
    broken_image: "Broken Image",
    non_nextgen_image: "Non-Next-Gen Image",
    large_page: "Oversized Page",
    redirect_chain: "Redirect Chain",
    redirect_loop: "Redirect Loop",
    mixed_content: "Mixed Content",
    schema_error: "Schema Validation Error",
    missing_schema: "Missing Schema",
    schema_validation_error: "Schema Validation Errors",
    missing_og_tags: "Missing Open Graph Tags",
    missing_hsts: "Missing HSTS",
    hreflang_error: "Hreflang Error",
    sitemap_error: "Sitemap Issue",
    missing_from_sitemap: "Missing from Sitemap",
    performance_diagnostics: "Performance Diagnostics",
    keyword_cannibalization: "Keyword Cannibalization",
    image_missing_dimensions: "Image Missing Dimensions",
    core_web_vitals: "Core Web Vitals",
  };

  return labels[issue.issue_type] || "Issue Details";
}

const stringifyEvidenceValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "")
    return "Not available";
  if (Array.isArray(value)) return value.join(" -> ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const getEvidenceRows = (issue: PageIssue) => {
  const details = (issue.issue_details || {}) as Record<string, unknown>;
  const priorityKeys = [
    "source_url",
    "target_url",
    "image_url",
    "image_filename",
    "selector",
    "status_code",
    "http_status_code",
    "response_time_ms",
    "indexability",
    "canonical_url",
    "redirect_chain",
    "final_url",
    "current_alt_text",
    "current_title",
    "current_meta_description",
    "current_h1s",
  ];
  const rows: Array<[string, unknown]> = [];
  for (const key of priorityKeys) {
    if (Object.prototype.hasOwnProperty.call(details, key)) {
      rows.push([key, details[key]]);
    }
  }
  for (const entry of Object.entries(details)) {
    if (rows.length >= 12) break;
    if (!rows.some(([key]) => key === entry[0])) rows.push(entry);
  }
  return rows;
};

// Maps the OG markers Screaming Frog reports as missing (issue_details.missing
// values like "og:title") to the structured suggestion keys the backend returns
// in proposed_value ({ og_title, og_description, og_image, og_url, og_type }).
type OgFieldKey =
  | "og_title"
  | "og_description"
  | "og_image"
  | "og_url"
  | "og_type";

const OG_FIELD_KEY_MAP: Record<string, OgFieldKey> = {
  "og:title": "og_title",
  "og:description": "og_description",
  "og:image": "og_image",
  "og:url": "og_url",
  "og:type": "og_type",
};

const OG_FIELD_LABELS: Record<string, string> = {
  "og:title": "OG Title",
  "og:description": "OG Description",
  "og:image": "OG Image",
  "og:url": "OG URL",
  "og:type": "OG Type",
};

// Specialized before/after card for missing_og_tags. Renders only the OG fields
// flagged in issue_details.missing; each shows the current (empty) value vs. the
// LLM-proposed value. proposed_value may arrive as a JSON string or object (and
// the suggestion may also land in proposed_fix/approved_value) — parse
// defensively. Falls back to the generic diff if no OG fields are recognized.
function MissingOgTagsDiffCard({ issue }: { issue: PageIssue }) {
  const details = (issue.issue_details ?? {}) as { missing?: unknown };
  const missing = Array.isArray(details.missing)
    ? (details.missing as string[])
    : [];
  const fields = missing.filter((field) => field in OG_FIELD_KEY_MAP);

  if (fields.length === 0) {
    return <GenericDiffCard issue={issue} />;
  }

  const proposed =
    parseFixObject(issue.approved_value) ??
    parseFixObject(issue.proposed_fix) ??
    parseFixObject(issue.proposed_value) ??
    {};

  return (
    <div className="space-y-4">
      {/* Say what applying does — bare before/after per tag left users guessing (GH #599). */}
      <p className="text-xs text-muted-foreground">
        Apply writes these Open Graph tags to the page (via the SEO plugin) so social
        shares show the right title, description, and image.
      </p>
      {fields.map((field) => {
        const key = OG_FIELD_KEY_MAP[field];
        const rawAfter = proposed[key];
        const after = typeof rawAfter === "string" ? rawAfter.trim() : "";
        return (
          <div
            key={field}
            className="space-y-3 rounded-md border bg-card p-4"
          >
            <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {OG_FIELD_LABELS[field] ?? field}
              <span className="font-mono text-[10px] normal-case text-muted-foreground">
                {field}
              </span>
            </div>
            <div>
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Before
              </div>
              <pre className="whitespace-pre-wrap break-words rounded-md border bg-muted p-3 text-sm text-foreground">
                <span className="italic text-muted-foreground">
                  (empty / not set)
                </span>
              </pre>
            </div>
            <div>
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                After (proposed)
              </div>
              <pre className="whitespace-pre-wrap break-words rounded-md border border-teal/40 bg-teal-light p-3 text-sm text-foreground">
                {after || (
                  <span className="italic text-muted-foreground">
                    Generate a fix to see the proposed value.
                  </span>
                )}
              </pre>
            </div>
          </div>
        );
      })}
    </div>
  );
}


/** Text of the existing heading staged for promotion, if the operator picked one. */
function promoteChoiceText(value: ProposedFix | null): string | null {
  if (!value || typeof value !== "object") return null;
  const choice = (value as { promote_existing?: { text?: string } }).promote_existing;
  return choice?.text ?? null;
}

/**
 * Guard for TextDiffCard's onEdit in the H1 case: the card re-emits
 * ``{new_h1}`` on every draft reset (mount, issue refetch), and an unguarded
 * setEditedValue silently ERASED a staged promote-selection — the apply then
 * went out value-less ("No H1 value provided", 2026-08-16). A promote choice
 * is an explicit operator action, so a passive re-emit (empty, or merely the
 * stored suggestion) must not replace it; only a genuine typed override does.
 */
function preservePromoteChoice(
  prev: ProposedFix | null,
  incoming: ProposedFix,
  storedSuggestionH1: string,
): ProposedFix {
  const choice = promoteChoiceText(prev);
  if (!choice) return incoming;
  if (incoming && typeof incoming === "object" && "promote_existing" in incoming) {
    return incoming; // a new picker selection always wins
  }
  const typed = (
    (incoming as { new_h1?: string })?.new_h1 ?? ""
  ).trim();
  const passive = typed === "" || typed === storedSuggestionH1.trim();
  return passive ? (prev as ProposedFix) : incoming;
}

export function IssueDiffDrawer({
  issue,
  open,
  onClose,
  onReject,
  onApply,
  onPreview,
  onVerify,
  onGenerateSuggestion,
  isGeneratingSuggestion = false,
  isApplying = false,
}: IssueDiffDrawerProps) {
  const centralChat = useOptionalCentralChatBridge();
  const [editedValue, setEditedValue] = useState<ProposedFix | null>(null);
  // missing_from_sitemap apply gate, reported up by MissingFromSitemapCard once
  // it has diagnosed the cause. Default = NOT applyable: until the cause is
  // known (and, for a confirmable cause, deliberately confirmed) Apply stays
  // disabled. This is what makes a site-wide sitemap change impossible without
  // the explicit tick — `confirmSiteWide` is the only source of confirm_site_wide.
  const [sitemapGate, setSitemapGate] = useState<SitemapApplyGate>({
    decision: null,
    canApply: false,
    confirmSiteWide: false,
  });
  const [showFullPageDetails, setShowFullPageDetails] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  // Opt-in to the separate, human-delivered Agile & Co service. Never fires
  // without the confirm dialog — the client has to read what they're agreeing to.
  const [expertHelpOpen, setExpertHelpOpen] = useState(false);
  const requestExpertHelp = useRequestExpertHelp();
  const [modalTab, setModalTab] = useState<"fix" | "details" | "history">(
    "fix",
  );

  // Reset the edited draft when the user opens a DIFFERENT issue.
  //
  // This is deliberately a render-phase reset, not an effect. As an effect it
  // fought the diff cards: React runs CHILD effects before PARENT ones, so
  // SeoMetaEditCard (and every other card that lifts its initial draft up via
  // onEdit on mount) seeded editedValue and this effect then immediately wiped
  // it back to null in the same commit. The visible symptom was Apply greyed
  // out right after Generate — the suggestion was sitting in the field, but the
  // drawer had thrown its copy away, so the apply gates saw "no value" until
  // the operator typed a character and re-fired the child's effect alone.
  //
  // Adjusting state during render (React's documented pattern for props-derived
  // state) runs BEFORE children render and therefore before they seed, so the
  // seed survives and Apply is live as soon as a fix exists.
  const [lastIssueId, setLastIssueId] = useState<string | null>(
    issue?.id ?? null,
  );
  if ((issue?.id ?? null) !== lastIssueId) {
    setLastIssueId(issue?.id ?? null);
    setEditedValue(null);
    setShowFullPageDetails(false);
    setModalTab("fix");
  }

  const renderDiffCard = () => {
    if (!issue) return null;

    switch (issue.issue_type) {
      case "title_meta":
      case "h1":
      case "h1_missing":
      case "h1_multiple":
      case "h1_too_long": {
        const storedH1 = String(
          (parseFixObject(issue.approved_value) ??
            parseFixObject(issue.proposed_fix) ??
            parseFixObject(issue.proposed_value))?.new_h1 ?? "",
        );
        const promoteSelected = promoteChoiceText(editedValue);
        return (
          <div className="space-y-3">
            {/* Only a MISSING H1 can be satisfied by promoting an existing
                heading. The other H1 types already have an H1 to rewrite or
                collapse, so offering "use existing text" there would be a
                different (and wrong) operation. NOTE: missing-H1 rows usually
                arrive as the legacy composite issue_type "h1" with an
                issue_details.h1_missing flag — checking the atomic type alone
                silently skipped them (the picker never mounted). */}
            {isMissingH1Issue(issue) && (
              <H1CandidatePicker
                issue={issue}
                onEdit={setEditedValue}
                selectedText={promoteSelected}
                onClear={() =>
                  // Restore the generated heading rather than null: the card
                  // only re-emits its suggestion on draft resets, so a null
                  // editedValue would ship a value-less apply — the exact
                  // 2026-08-16 "No H1 value provided" incident.
                  setEditedValue({ new_h1: storedH1 } as ProposedFix)
                }
              />
            )}
            <TextDiffCard
              key={`${issue.id}:${getSuggestionVersion(issue)}`}
              issue={issue}
              h1EditingDisabled={Boolean(promoteSelected)}
              onEdit={(v) =>
                setEditedValue((prev) => preservePromoteChoice(prev, v, storedH1))
              }
            />
          </div>
        );
      }
      case "alt_text":
        return (
          <ImageAltDiffCard
            key={`${issue.id}:${getSuggestionVersion(issue)}`}
            issue={issue}
            onEdit={setEditedValue}
          />
        );
      case "redirect":
      case "redirect_chain":
        return (
          <div className="space-y-4">
            <RedirectChainCard issue={issue} />
            <ResolverSuggestionCard issue={issue} onEdit={setEditedValue} />
          </div>
        );
      case "broken_link":
        return <BrokenLinkCard issue={issue} />;
      case "broken_internal":
        return (
          <div className="space-y-4">
            <BrokenLinkCard issue={issue} />
            <ResolverSuggestionCard issue={issue} onEdit={setEditedValue} />
          </div>
        );
      case "links_to_redirect":
        // Source-page-anchored evidence (page -> its N internal links that 3xx
        // redirect) + editable resolver suggestion. Mirrors links_to_broken; the
        // dedicated card never renders the page as its own "broken URL" the way
        // BrokenLinkCard did for this type.
        return (
          <div className="space-y-4">
            <LinksToRedirectCard issue={issue} />
            <ResolverSuggestionCard issue={issue} onEdit={setEditedValue} />
          </div>
        );
      case "links_to_broken":
        // Source-page-anchored evidence (page -> its N dead 4xx/5xx internal
        // targets) + editable resolver suggestion. Mirrors broken_internal: the
        // Generate button (AI_SUGGESTION_TYPES) produces the repoint/remove edits
        // that ResolverSuggestionCard renders and lets the specialist adjust.
        return (
          <div className="space-y-4">
            <LinksToBrokenCard issue={issue} />
            <ResolverSuggestionCard issue={issue} onEdit={setEditedValue} />
          </div>
        );
      case "canonical":
        return <CanonicalCard issue={issue} />;
      case "duplicate_title":
        return (
          <DuplicateTitleComparison issue={issue} onEdit={setEditedValue} />
        );
      case "duplicate_meta":
        return (
          <DuplicateMetaComparison issue={issue} onEdit={setEditedValue} />
        );
      case "thin_content":
        return <ThinContentCard issue={issue} onEdit={setEditedValue} />;
      case "indexability":
        return (
          <div className="space-y-4">
            <IndexabilityCard issue={issue} />
            <ResolverSuggestionCard issue={issue} onEdit={setEditedValue} />
          </div>
        );
      case "noindex_important":
        // Importance-evidence card (sitemap + inlinks + depth) with a "What
        // Apply changes" panel. The detector already staged the deterministic
        // set_index fix in proposed_value, so there is no Generate step and no
        // resolver editor — Apply flips the SEO-plugin robots meta to index.
        return <NoindexImportantCard issue={issue} />;
      case "sitemap_error":
        return (
          <div className="space-y-4">
            <SitemapErrorCard issue={issue} />
            <ResolverSuggestionCard issue={issue} onEdit={setEditedValue} />
          </div>
        );
      case "slow_response":
        return <SlowResponseCard issue={issue} />;
      case "near_duplicate":
        return <NearDuplicateCard issue={issue} />;
      case "low_readability":
        return <LowReadabilityDiffCard issue={issue} onEdit={setEditedValue} />;
      case "low_text_ratio":
        return <LowTextRatioCard issue={issue} />;
      case "stale_content":
        return <StaleContentCard issue={issue} />;
      case "orphan_page":
        return <OrphanPageCard issue={issue} onEdit={setEditedValue} />;
      case "broken_external":
        // Flag ON: read-only context card + editable resolver suggestion
        // (mirrors broken_internal). Flag OFF: original read-only-only behavior.
        return BROKEN_EXTERNAL_FIX_ENABLED ? (
          <div className="space-y-4">
            <BrokenExternalCard issue={issue} />
            <ResolverSuggestionCard issue={issue} onEdit={setEditedValue} />
          </div>
        ) : (
          <BrokenExternalCard issue={issue} />
        );
      case "deep_page":
        return <DeepPageCard issue={issue} />;
      case "unsafe_crossorigin":
        return <UnsafeCrossoriginCard issue={issue} />;
      case "nofollow_internal":
        return <NofollowInternalCard issue={issue} />;
      case "low_internal_links":
        return (
          <div className="space-y-4">
            <LowInternalLinksCard issue={issue} />
            <InternalLinkSuggestionCard issue={issue} onEdit={setEditedValue} />
          </div>
        );
      case "title_too_short":
      case "page_title_below_30_chars":
        return (
          <div className="space-y-4">
            <TitleTooShortCard issue={issue} />
            <SeoMetaEditCard
              key={`${issue.id}:${getSuggestionVersion(issue)}`}
              issue={issue}
              onEdit={setEditedValue}
            />
          </div>
        );
      case "page_title_same_as_h1":
      case "page_title_missing":
      case "page_title_over_60_chars":
      case "meta_missing":
      case "meta_description_over_155_chars":
      case "meta_description_below_70_chars":
        // No GenericDiffCard here: it dumps the raw original_value/proposed_fix
        // JSON, which is redundant + ugly. SeoMetaEditCard already shows the
        // current value (before) and the editable suggested title/meta (after)
        // with character counts.
        return (
          <div className="space-y-4">
            <SeoMetaEditCard
              key={`${issue.id}:${getSuggestionVersion(issue)}`}
              issue={issue}
              onEdit={setEditedValue}
            />
          </div>
        );
      case "serp_title_truncated":
        return (
          <div className="space-y-4">
            <SerpTitleTruncatedCard issue={issue} />
            <SeoMetaEditCard
              key={`${issue.id}:${getSuggestionVersion(issue)}`}
              issue={issue}
              onEdit={setEditedValue}
            />
          </div>
        );
      case "serp_meta_truncated":
        return (
          <div className="space-y-4">
            <SerpMetaTruncatedCard issue={issue} />
            <SeoMetaEditCard
              key={`${issue.id}:${getSuggestionVersion(issue)}`}
              issue={issue}
              onEdit={setEditedValue}
            />
          </div>
        );
      case "multiple_meta":
        // Auto-fixable: the inline card auto-loads the LLM single-meta
        // recommendation and lifts it up via onEdit, so Apply writes exactly the
        // description shown here (excerpt + SEO-plugin meta) with no second modal.
        return <MultipleMetaCard issue={issue} onEdit={setEditedValue} />;
      case "duplicate_h1":
        return (
          <div className="space-y-4">
            <DuplicateH1Card issue={issue} />
            <DuplicateH1EditCard
              key={`${issue.id}:${getSuggestionVersion(issue)}`}
              issue={issue}
              onEdit={setEditedValue}
            />
          </div>
        );
      case "large_image":
        return <LargeImageCard issue={issue} />;
      case "broken_image":
        return <BrokenImageCard issue={issue} />;
      case "non_nextgen_image":
        return <NonNextgenImageCard issue={issue} />;
      case "large_page":
        return <LargePageCard issue={issue} />;
      case "redirect_loop":
        return (
          <div className="space-y-4">
            <RedirectLoopCard issue={issue} />
            <ResolverSuggestionCard issue={issue} onEdit={setEditedValue} />
          </div>
        );
      case "mixed_content":
        return <MixedContentCard issue={issue} />;
      case "schema_error":
        return <SchemaErrorCard issue={issue} />;
      case "missing_schema":
      case "schema_validation_error":
        return <MissingSchemaCard issue={issue} onEdit={setEditedValue} />;
      case "missing_compression":
        return <MissingCompressionCard issue={issue} />;
      case "hreflang_error":
        return <HreflangCard issue={issue} />;
      case "keyword_cannibalization":
        return (
          <KeywordCannibalizationCard issue={issue} onEdit={setEditedValue} />
        );
      case "image_missing_dimensions":
        return <ImageMissingDimensionsCard issue={issue} />;
      case "core_web_vitals":
        return <CoreWebVitalsCard issue={issue} />;
      case "missing_og_tags":
        return <MissingOgTagsDiffCard issue={issue} />;
      case "missing_from_sitemap":
        // NOT a value diff: original_value/proposed_value are both null here, so
        // GenericDiffCard would render an empty before/after. The card diagnoses
        // WHY the page is excluded and reports the apply gate up (refused causes
        // can't apply at all; a site-wide un-exclusion only after the user ticks
        // the confirmation).
        return (
          <MissingFromSitemapCard issue={issue} onGateChange={setSitemapGate} />
        );
      default:
        // A3: never leave a type without a before/after — fall back to a
        // generic original_value -> proposed diff.
        return <GenericDiffCard issue={issue} />;
    }
  };

  const handleReject = () => {
    if (!issue) return;
    onReject(issue.id);
  };

  const handleApply = () => {
    if (!issue || !onApply) return;
    // noindex_important applies through the normal (non-review) path, but the
    // write changes how the page appears in Google, so confirm it — the same
    // guard pattern the review-only override uses.
    if (issue.issue_type === "noindex_important") {
      const ok = window.confirm(
        "Apply this fix?\n\nThis will remove the noindex and let Google index the page.",
      );
      if (!ok) return;
    }
    // missing_from_sitemap: refuse to even request the apply unless the card's
    // diagnosis says it may proceed (auto_apply, or a confirmable cause the user
    // ticked). confirm_site_wide only ever travels with a ticked site-wide box.
    if (issue.issue_type === "missing_from_sitemap") {
      if (!sitemapGate.canApply) return;
      onApply(issue.id, reviewedFixValueForApply(issue, editedValue), {
        ...(sitemapGate.confirmSiteWide ? { confirm_site_wide: true } : {}),
      });
      return;
    }
    onApply(issue.id, reviewedFixValueForApply(issue, editedValue));
  };

  // "Apply anyway" for a review-only finding — overrides the safeguard (e.g.
  // removes a noindex so the page can rank). Confirmed because it changes how
  // the page appears in Google.
  const handleApplyOverride = () => {
    if (!issue || !onApply) return;
    const ok = window.confirm(
      "Apply anyway?\n\nThis overrides the review safeguard and changes the live page " +
        "(e.g. removes its noindex so it can be indexed by Google). Only continue if " +
        "you're sure this page SHOULD rank.",
    );
    if (!ok) return;
    onApply(issue.id, reviewedFixValueForApply(issue, editedValue), {
      force: true,
    });
  };

  // Rewriting published copy on a page we could not classify. The confirm
  // names what is unknown and what is at stake rather than asking "are you
  // sure" — and says the write is undoable, because it is (before_state).
  const handleThinContentOverride = () => {
    if (!issue || !onApply) return;
    const ok = window.confirm(
      "Apply anyway?\n\nWe could not confirm whether this is a blog post or a " +
        "conversion page, so it was treated as a conversion page. Applying " +
        "rewrites published copy on a page that may drive conversions.\n\n" +
        "It is revertible from fix history.",
    );
    if (!ok) return;
    onApply(issue.id, reviewedFixValueForApply(issue, editedValue), {
      force: true,
    });
  };

  const handlePreview = () => {
    if (!issue || !onPreview) return;
    // Carry the sitemap confirmation (if the user already ticked it) into the
    // preview so the confirm step reflects the same consent the Apply path uses.
    onPreview(issue.id, reviewedFixValueForApply(issue, editedValue), {
      ...(sitemapGate.confirmSiteWide ? { confirm_site_wide: true } : {}),
    });
  };

  const handleVerify = () => {
    if (!issue || !onVerify) return;
    onVerify(issue.id);
  };

  const handleChatWithAi = () => {
    if (!issue || !centralChat) return;
    centralChat.openIssueChat(
      {
        issue_id: issue.id,
        issue_type: issue.issue_type,
        page_title: issue.page_title ?? null,
        wordpress_id: issue.wordpress_id ?? null,
        run_id: issue.run_id ?? null,
      },
      "",
    );
    onClose();
  };

  const status = (issue?.fix_status as IssueStatus | undefined) ?? "pending";
  const displayStatus = issue
    ? getReadabilityDisplayStatus(
        issue as unknown as Parameters<typeof getReadabilityDisplayStatus>[0],
      )
    : status;
  const severity = issue ? calculateSeverity(issue) : null;
  // Page-body fixes can still be excluded, but SEO title/meta fixes write only
  // Yoast/RankMath meta keys and are safe for confirmed pages as well as posts.
  const isSeoMetaEditIssue = Boolean(
    issue && SEO_META_EDIT_TYPES.has(issue.issue_type),
  );
  // multiple_meta writes ONLY the post excerpt + SEO-plugin meta description
  // (never the page-builder body), so — exactly like the SEO title/meta types —
  // it is safe to apply on confirmed pages too. Exempt it from the page auto-fix
  // exclusion that protects page-builder bodies.
  const isMultipleMetaApplyIssue = issue?.issue_type === "multiple_meta";
  // missing_og_tags writes SEO-plugin OG meta (Yoast/RankMath) and verifies the
  // rendered head; it does not edit page-builder body content, so it follows the
  // duplicate_meta page/apply gate rather than the body-edit exclusion.
  const isOpenGraphMetaApplyIssue = issue?.issue_type === "missing_og_tags";
  // missing_schema + schema_validation_error apply-ability is governed by the fix
  // payload's apply_disabled (not the blog-vs-page body gate), so both are exempt
  // from the page-only exclusion and share the JSON-LD card + apply gating below.
  const isMissingSchemaIssue =
    issue?.issue_type === "missing_schema" ||
    issue?.issue_type === "schema_validation_error";
  // hreflang_error rewrites ONLY the page's head-level <link rel="alternate">
  // tags (never the page-builder body) and hreflang is inherently a page-level
  // concern, so — like the SEO title/meta types — exempt it from the page
  // auto-fix exclusion that protects page-builder bodies. The preview endpoint
  // (is_auto_fixable per reason code) stays the apply authority.
  const isHreflangIssue = issue?.issue_type === "hreflang_error";
  // missing_from_sitemap writes SEO-plugin settings (sitemap exclusion lists,
  // post-type toggles, robots meta) and clears the sitemap cache — never the
  // page-builder body — so, like the SEO title/meta types, it is exempt from the
  // page auto-fix exclusion. Per-row authority stays with the preview's
  // diagnosed decision (sitemapGate), not the fix_target.
  const isSitemapMissingIssue = issue?.issue_type === "missing_from_sitemap";
  const isPageExcluded =
    issue?.fix_target === "page" &&
    !isSeoMetaEditIssue &&
    !isMultipleMetaApplyIssue &&
    !isOpenGraphMetaApplyIssue &&
    !isMissingSchemaIssue &&
    !isHreflangIssue &&
    !isSitemapMissingIssue;
  const isExcludedFromApply = isPageExcluded;
  // The diagnosed cause says this row must not be applied at all (a filter in
  // the site's own code, an unpublishable page) or is a false positive (front
  // page) — or it hasn't been diagnosed/confirmed yet.
  const sitemapApplyBlocked = Boolean(
    isSitemapMissingIssue && !sitemapGate.canApply,
  );
  // TERMINAL subset of the above: the preview came back `refuse` (a cause we
  // will not write around, e.g. sql_filter_present) or `not_an_issue` (false
  // positive — already_in_sitemap / front_or_blog_page). No tick, no edit, no
  // regeneration in this drawer can ever make Apply work, so the button is
  // removed rather than shown dead.
  //
  // ⚠️ Deliberately NOT terminal: decision === null (still diagnosing) and
  // decision === "needs_confirm" with the consent box un-ticked. The latter is
  // the excluded_by_filter / force-include override path — the user ticks the
  // box IN THIS DRAWER and Apply lights up. Hiding it there would hide the
  // override entirely.
  const sitemapApplyTerminal = Boolean(
    isSitemapMissingIssue &&
      (sitemapGate.decision === "refuse" ||
        sitemapGate.decision === "not_an_issue"),
  );
  const isLargeImage = issue?.issue_type === "large_image";
  const largeImageResultState = issue ? getLargeImageResultState(issue) : null;
  const isApplied = status === "applied";
  // A partial internal-link apply: some of the operator's selected sources were
  // written, the rest failed. The backend keeps the row 'applied' (the write is
  // real and must stay revertible) and carries the "Linked 1 of 3 selected
  // sources. …" explanation on apply_error, which is the ONLY signal that the
  // green badge doesn't mean the whole job got done. A successful full apply
  // clears apply_error, so a non-null error on an applied row means partial.
  const isPartiallyApplied = Boolean(isApplied && issue?.error);
  // Recommendation-only: guidance was saved but NOTHING was written to
  // WordPress, so no "saved in WordPress" banner and no Verify (nothing to
  // verify on the live page).
  const isRecommended = status === "recommended";
  const isRejected = status === "rejected";
  const isSkipped = status === "skipped";
  const isFailed = status === "failed";
  const isVerified = status === "verified";
  // "manual" = an expert-fix ticket was already filed for this issue.
  const isManualRequested = status === "manual";
  // The expert resolved their ticket by hand; the next crawl verifies it.
  const isManualApplied = status === "manual_applied";
  // Either way the issue belongs to the expert team right now — the client
  // must not be offered "Apply to WordPress" for work someone else owns.
  const isExpertOwned = isManualRequested || isManualApplied;
  const isReviewOnly = isReviewOnlyIssue(issue);
  const isReviewed = isReviewOnly && (isRejected || isSkipped);
  // Review-only issues the user can "Apply anyway" (force-override — e.g. flip a
  // noindex page to indexable). The backend revalidates per cause and may still
  // answer manual_required.
  const isOverridable = isOverridableReviewIssue(issue);
  const wpEditUrl = getWordPressEditUrl(issue);
  const isTerminalReviewStatus = isRejected || isSkipped;
  // Pre-apply publish-first check: for a source-page link fix on a non-terminal
  // issue, ask the backend whether the page is publicly live. If it 404s (page
  // unpublished), disable Apply and prompt the user to publish it first, rather
  // than only finding out when the apply resolves the issue to `obsolete`. Gated
  // to eligible types + pending/approved so no other issue triggers the check.
  const sourcePublishEligible = Boolean(
    open &&
      issue &&
      SOURCE_EDIT_ISSUE_TYPES.has(issue.issue_type) &&
      (status === "pending" || status === "approved"),
  );
  const { data: sourcePublishState } = useSourcePublishState(
    issue?.id,
    sourcePublishEligible,
  );
  const sourceNotPublic = Boolean(sourcePublishState?.source_not_public);

  // A failure with no self-serve recovery kind (plugin/key/creds) is a genuine
  // "needs a person" case — retrying Apply will just fail again. Replace Apply with
  // "Request expert fix" so the user isn't led into a doomed click.
  const { data: drawerRecovery } = useRecoveryActions(issue?.id, isFailed);
  const requestManualFix = useRequestManualFix();
  const isManualFailure = isFailed && !drawerRecovery?.kind;
  const isLowReadabilityIssue = issue?.issue_type === "low_readability";
  const isThinContentIssue = issue?.issue_type === "thin_content";
  const isInternalLinkIssue =
    issue?.issue_type === "orphan_page" ||
    issue?.issue_type === "low_internal_links";
  // Direct-apply types (H1): primary action is "Apply to WordPress" right after
  // a fix is generated — no separate review-state step, no blocking confirm modal. The
  // before/after preview stays available as optional inspection.
  const isDirectApply = Boolean(
    issue && isDirectApplyIssueType(issue.issue_type),
  );
  // multiple_meta is direct-apply (excerpt + SEO-plugin meta write) with a
  // meaningful before/after preview — the LLM-recommended single description —
  // so keep the preview button available alongside Generate fix / Apply.
  const isRecommendationPreviewIssue = Boolean(
    issue &&
      (issue.issue_type === "multiple_meta" ||
        (issue.issue_type as string) === "meta_description_multiple"),
  );
  // noindex_important has a deterministic staged fix (robots meta
  // noindex -> index), so the before/after preview is always meaningful —
  // surface the Preview button without requiring a generated suggestion.
  const isNoindexImportantIssue = issue?.issue_type === "noindex_important";
  const largeImageResultTerminal =
    largeImageResultState === "applied" ||
    largeImageResultState === "skipped" ||
    largeImageResultState === "protected" ||
    (!largeImageResultState && isApplied);
  const largeImageActionLabel =
    largeImageResultState === "applied"
      ? "Applied"
      : largeImageResultState === "skipped"
        ? "Skipped"
        : largeImageResultState === "protected"
          ? "Protected"
          : largeImageResultState === "failed" || isFailed
            ? "Retry optimize"
            : "Optimize image";
  const detectedAt = issue?.created_at
    ? new Date(issue.created_at).toLocaleString()
    : null;
  const typeHelp = issue ? getIssueTypeHelp(issue.issue_type) : null;
  const evidenceRows = issue ? getEvidenceRows(issue) : [];
  // broken_external is "deterministic" fixMode, but when the flag is on it DOES
  // apply to WordPress (remove/repoint) and has no /generate-suggestion path — its
  // value comes from the resolver editor (editedValue), not a stored suggestion.
  // Treat it as auto so the Apply button renders ("Apply to WordPress") instead of
  // requiring a stored suggestion (hasSuggestedFix), which a pending issue lacks.
  const isAutoMode =
    typeHelp?.fixMode === "auto" ||
    (BROKEN_EXTERNAL_FIX_ENABLED && issue?.issue_type === "broken_external");
  const canGenerateSuggestion = Boolean(
    issue &&
      onGenerateSuggestion &&
      AI_SUGGESTION_TYPES.has(issue.issue_type),
  );
  const hasSuggestedFix = Boolean(
    issue?.approved_value || issue?.proposed_fix || issue?.proposed_value,
  );
  // missing_schema: handoff (apply_disabled) => no live write, review/handoff
  // only. Applyable (Article/FAQ) => normal approve→apply once a fix exists.
  const isMissingSchemaHandoff = Boolean(
    issue && isMissingSchemaIssue && isMissingSchemaApplyDisabled(issue),
  );
  const isMissingSchemaApplyable = Boolean(
    isMissingSchemaIssue && hasSuggestedFix && !isMissingSchemaHandoff,
  );
  const needsSuggestionBeforeApply =
    Boolean(issue) &&
    canGenerateSuggestion &&
    !hasSuggestedFix &&
    !hasMeaningfulFixValue(editedValue);
  const needsSeoMetaEditValue =
    issue != null &&
    SEO_META_EDIT_TYPES.has(issue.issue_type) &&
    hasSuggestedFix &&
    !hasMeaningfulFixValue(editedValue);
  // multiple_meta: MultipleMetaCard always lifts the chosen description (or an
  // explicit empty value) into editedValue. Block Apply until one is chosen so
  // the backend never falls back to a silent server-side LLM pick.
  const needsMultipleMetaValue =
    Boolean(issue) &&
    issue?.issue_type === "multiple_meta" &&
    !hasMeaningfulFixValue(editedValue);
  const needsInternalLinkSourceSelection =
    Boolean(issue) &&
    (issue?.issue_type === "orphan_page" ||
      issue?.issue_type === "low_internal_links") &&
    hasSuggestedFix &&
    !hasInternalLinkSourceSelection(editedValue) &&
    !hasInternalLinkSourceSelection(issue?.approved_value) &&
    !hasInternalLinkSourceSelection(issue?.proposed_fix) &&
    !hasInternalLinkSourceSelection(issue?.proposed_value);
  const needsReadabilitySentenceRegeneration = isStaleReadabilitySentenceFix(
    issue as Parameters<typeof isStaleReadabilitySentenceFix>[0],
  );
  const needsReadabilitySentenceSelection =
    Boolean(issue) &&
    issue?.issue_type === "low_readability" &&
    hasSuggestedFix &&
    !needsReadabilitySentenceRegeneration &&
    !hasMeaningfulFixValue(editedValue);
  const isDuplicateComparison =
    issue?.issue_type === "duplicate_title" ||
    issue?.issue_type === "duplicate_meta";

  // Phase 1 thin_content blog-only additive apply gate. Resolve the apply state
  // (blog/CRO/grounded/appendable) once; the footer renders the apply button for
  // thin_content ONLY when blog-eligible, never for page/CRO/unknown.
  const thinContentApply =
    issue && isThinContentIssue
      ? readThinContentApplyState(issue, editedValue)
      : null;
  const isThinContentBlogEligible = Boolean(
    isThinContentIssue &&
    hasSuggestedFix &&
    thinContentApply &&
    thinContentApply.isBlog &&
    !thinContentApply.croRisk,
  );
  // "Apply anyway" for a page the classifier never actually judged. It defaults
  // to conversion-page-as-precaution when it cannot tell (source "default"), and
  // that default was blocking ordinary service/location pages. A page POSITIVELY
  // identified as a conversion page is deliberately NOT offered this.
  const thinContentOverridable = Boolean(
    isThinContentIssue &&
    hasSuggestedFix &&
    thinContentApply &&
    !isThinContentBlogEligible &&
    thinContentApply.unconfirmed &&
    thinContentApply.grounded &&
    thinContentApply.hasApplicableContent,
  );
  // grounded:false or nothing-appendable blocks the apply (backend hard-blocks
  // ungrounded too); the button stays visible-but-disabled for blog so the user
  // sees why via the helper/card, matching the preview's canConfirm.
  const thinContentApplyBlocked = Boolean(
    isThinContentBlogEligible &&
    thinContentApply &&
    (!thinContentApply.grounded || !thinContentApply.hasApplicableContent),
  );

  // Apply is STRUCTURALLY impossible for this row — nothing the user can do in
  // this drawer will ever enable it. Render no Apply button (and no before/after
  // preview of a write that can't happen) instead of a dead disabled one.
  //
  // Only permanently-unavailable conditions belong here. Conditions the user can
  // clear themselves (un-ticked sitemap consent, missing suggestion/selection,
  // in-flight apply) stay in the `disabled` list so the affordance — and the way
  // to unlock it — remains visible.
  const applyPermanentlyUnavailable = Boolean(
    sitemapApplyTerminal || sourceNotPublic || isPageExcluded,
  );
  // The before/after preview is a preview of the WordPress write, so it dies with
  // the Apply button. The ONE exception: recommendation-preview types, where the
  // preview fetch is what PRODUCES the LLM recommendation — it has standalone
  // value even when nothing will be written (e.g. a page-excluded
  // meta_description_multiple).
  const previewPermanentlyUnavailable =
    applyPermanentlyUnavailable && !isRecommendationPreviewIssue;

  // Universal "needs a human" fallback (GH #614): any actionable, non-review-only
  // issue with NO self-serve apply path — recommendation-only (multiple_meta), a
  // pending row with no suggestion — must offer an expert ticket instead of
  // dead-ending. Review-only is handled by Site A / "Apply anyway"; a hard failure
  // with no recovery by Site C (isManualFailure). `canSelfApply` mirrors the footer
  // Apply guard so the fallback appears ONLY when Apply can't — it never competes
  // with a working Apply, so the happy path stays uncluttered. request-manual-fix
  // accepts any issue in any status, so no backend gating.
  const canSelfApply = Boolean(
    onApply &&
      !applyPermanentlyUnavailable &&
      (isThinContentIssue
        ? isThinContentBlogEligible
        : isMissingSchemaIssue
          ? isMissingSchemaApplyable
          : isAutoMode || hasSuggestedFix),
  );
  const showExpertFallback = Boolean(
    issue &&
      !isReviewOnly &&
      !isManualRequested &&
      !isApplied &&
      !isVerified &&
      !isRecommended &&
      !isTerminalReviewStatus &&
      status !== "obsolete" &&
      status !== "protected" &&
      !isManualFailure &&
      !canSelfApply &&
      // If the user can still generate a fix, that's the self-serve path — don't
      // pre-empt it with an expert ticket.
      !canGenerateSuggestion,
  );

  // ONE expert affordance per issue. The drawer already offers "Request expert
  // fix" in several places — the review-only block, the 3-failed-attempts block,
  // a hard failure (isManualFailure), and the universal fallback — and each
  // files the same ticket. Adding a second, always-on button next to any of them
  // asks the operator to choose between two doors to the same room.
  //
  // So "Get expert help" is the CLIENT-facing opt-in for issues that offer no
  // expert route of their own: it carries the consent wording, where the buttons
  // above are the internal "this needs a person" handoff.
  const hasExistingExpertAction = Boolean(
    isManualFailure ||
      showExpertFallback ||
      // Review-only block (Site A) renders its own expert-fix button.
      (isReviewOnly && !isReviewed && !isManualRequested && !isOverridable) ||
      // The 3-failed-generation-attempts block renders one too.
      Number(
        (issue?.issue_details as { auto_suggest?: { attempts?: number } })
          ?.auto_suggest?.attempts ?? 0,
      ) >= 3,
  );
  const showExpertHelp = shouldShowExpertHelp({
    hasIssue: Boolean(issue),
    hasExistingExpertAction,
    isManualRequested,
    isApplied,
    isVerified,
    isTerminalReviewStatus,
    status,
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          setIsMaximized(false);
          onClose();
        }
      }}
    >
      <DialogContent
        className={`flex flex-col gap-0 overflow-hidden p-0 ${
          isMaximized
            ? "h-[96vh] max-h-[96vh] w-[98vw] max-w-[98vw]"
            : isDuplicateComparison
              ? "max-h-[88vh] w-[92vw] sm:max-w-[min(980px,calc(100vw-2rem))]"
              : "max-h-[88vh] w-[92vw] max-w-3xl"
        }`}
      >
        <FixingOverlay show={Boolean(isApplying)} label="Applying fix…" />
        <button
          type="button"
          onClick={() => setIsMaximized((m) => !m)}
          aria-label={isMaximized ? "Restore" : "Maximize"}
          className="absolute right-12 top-4 z-10 rounded-sm p-1 text-muted-foreground opacity-70 transition-opacity hover:text-foreground hover:opacity-100"
        >
          {isMaximized ? (
            <Minimize2 className="h-4 w-4" />
          ) : (
            <Maximize2 className="h-4 w-4" />
          )}
        </button>
        <DialogHeader className="shrink-0 space-y-3 border-b border-border px-6 pb-4 pr-16 pt-6 text-left">
          <div className="flex items-start justify-between gap-3">
            <DialogTitle className="text-base font-semibold leading-tight">
              {getIssueTitle(issue)}
            </DialogTitle>
            <div className="flex items-center gap-2 shrink-0">
              {severity && <SeverityBadge severity={severity} size="sm" />}
              {issue && (
                <StatusBadge
                  status={displayStatus}
                  size="sm"
                  resultState={isLargeImage ? largeImageResultState : null}
                  reviewOnly={isReviewOnly}
                />
              )}
            </div>
          </div>
          {issue?.page_title && (
            <p
              className="text-sm text-foreground truncate"
              title={issue.page_title}
            >
              {issue.page_title}
            </p>
          )}
          {issue?.page_url && (
            <a
              href={issue.page_url}
              onClick={(e) => openWithCacheBuster(e, issue.page_url)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-coral hover:text-coral-hover truncate max-w-full"
              title={issue.page_url}
            >
              <span className="truncate">{issue.page_url}</span>
              <ExternalLink className="w-3 h-3 shrink-0" />
            </a>
          )}
        </DialogHeader>

        <div className="flex shrink-0 gap-1 border-b border-border px-6">
          {(
            [
              { key: "fix", label: "Fix" },
              { key: "details", label: "Details" },
              { key: "history", label: "History" },
            ] as const
          ).map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setModalTab(tab.key)}
              className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                modalTab === tab.key
                  ? "border-coral text-coral"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-6 space-y-6">
          {isFailed && issue && (
            <RecoveryActions
              issueId={issue.id}
              isFailed={isFailed}
              onNavigate={onClose}
            />
          )}
          {isPartiallyApplied && (
            <div
              data-testid="partial-apply-notice"
              className="flex gap-3 rounded-md border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/40"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <div className="space-y-1 text-sm">
                <p className="font-semibold text-foreground">
                  Partially applied
                </p>
                <p className="text-muted-foreground">{issue?.error}</p>
                {/* The Revert lives in the History TAB, not further down this
                    one. Saying "below" sent operators scrolling the Fix tab
                    looking for a control that was never on it — so link
                    straight to it instead of describing where it is. */}
                <p className="text-muted-foreground">
                  The links that did land are live. To undo them, open{" "}
                  <button
                    type="button"
                    data-testid="partial-apply-history-link"
                    onClick={() => setModalTab("history")}
                    className="font-medium text-coral underline underline-offset-2 hover:text-coral-hover"
                  >
                    fix history
                  </button>
                  .
                </p>
              </div>
            </div>
          )}
          {sourceNotPublic && issue && (
            <PublishFirstNotice
              issueId={issue.id}
              message={sourcePublishState?.message}
              onNavigate={onClose}
            />
          )}
          {modalTab === "details" && (
            <>
              {typeHelp && (
                <section className="rounded-md border bg-muted p-4">
                  <h3 className="text-sm font-semibold text-foreground">
                    {typeHelp.title}
                  </h3>
                  <p className="mt-2 text-sm text-foreground">
                    {typeHelp.description}
                  </p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {typeHelp.impact}
                  </p>
                  <p className="mt-2 text-sm text-foreground">
                    <span className="font-medium">Typical fix:</span>{" "}
                    {typeHelp.typicalFix}
                  </p>
                </section>
              )}

              {issue && (
                <section>
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Page Details
                    </h3>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setShowFullPageDetails((current) => !current)
                      }
                      className="h-7 px-2 text-xs text-muted-foreground"
                    >
                      {showFullPageDetails ? (
                        <Minimize2 className="mr-1.5 h-3.5 w-3.5" />
                      ) : (
                        <Maximize2 className="mr-1.5 h-3.5 w-3.5" />
                      )}
                      {showFullPageDetails ? "Collapse" : "Show full"}
                    </Button>
                  </div>
                  <dl className="grid grid-cols-3 gap-y-2 gap-x-4 text-sm">
                    {issue.page_title && (
                      <>
                        <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                          Title
                        </dt>
                        <dd
                          className={`col-span-2 text-foreground ${
                            showFullPageDetails
                              ? "whitespace-pre-wrap break-words"
                              : "max-h-10 overflow-hidden break-words"
                          }`}
                          title={issue.page_title}
                        >
                          {issue.page_title}
                        </dd>
                      </>
                    )}
                    <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                      URL
                    </dt>
                    <dd
                      className={`col-span-2 text-foreground ${
                        showFullPageDetails
                          ? "break-all"
                          : "max-h-10 overflow-hidden break-all"
                      }`}
                      title={issue.page_url || undefined}
                    >
                      {issue.page_url || "—"}
                    </dd>
                    {detectedAt && (
                      <>
                        <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                          Detected
                        </dt>
                        <dd className="col-span-2 text-foreground">
                          {detectedAt}
                        </dd>
                      </>
                    )}
                    {severity && (
                      <>
                        <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                          Priority
                        </dt>
                        <dd className="col-span-2 text-foreground">
                          {severity.charAt(0).toUpperCase() + severity.slice(1)}
                        </dd>
                      </>
                    )}
                  </dl>
                </section>
              )}

              {issue && evidenceRows.length > 0 && (
                <section>
                  <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
                    Evidence / Source Data
                  </h3>
                  <dl className="rounded-md border bg-card text-sm">
                    {evidenceRows.map(([key, value]) => (
                      <div
                        key={key}
                        className="grid grid-cols-3 gap-3 border-b px-3 py-2 last:border-b-0"
                      >
                        <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                          {key.replace(/_/g, " ")}
                        </dt>
                        <dd className="col-span-2 break-words text-foreground">
                          {stringifyEvidenceValue(value)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </section>
              )}
            </>
          )}

          {modalTab === "fix" && isReviewOnly && (
            <section className="space-y-4">
              <div
                className={`rounded-md border p-4 ${
                  isReviewed
                    ? "border-teal/40 bg-teal-light"
                    : "border-coral/40 bg-coral-light"
                }`}
              >
                <div
                  className={`flex items-center gap-2 text-sm font-semibold ${
                    isReviewed ? "text-teal-dark" : "text-coral-dark"
                  }`}
                >
                  {isReviewed ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : (
                    <AlertCircle className="h-4 w-4" />
                  )}
                  {isReviewed
                    ? "Reviewed — no change made"
                    : "Review only — no automatic fix"}
                </div>
                <p className="mt-2 text-sm text-foreground">
                  {isReviewed
                    ? "You marked this as intentional. Nothing was changed in WordPress."
                    : isOverridable
                      ? "This finding is not auto-applied because it is usually intentional, so nothing is queued. Keep it as-is if that is correct — or, if this page SHOULD rank, use “Apply anyway” to override (it changes the live page), or fix it by hand in WordPress and re-check."
                      : "This finding can't be auto-applied — it needs a hands-on fix (e.g. a redirect owned by another plugin, or editorial work). Keep it as-is if it's intentional, fix it by hand in WordPress and re-check, or send it to an AgileCo expert below."}
                </p>
                {/* A review-only result from an AI-suggestion generator is often
                    transient — the resolver couldn't resolve a safe target THIS
                    run (e.g. links_to_redirect whose targets live in page-builder
                    data). Keep a Regenerate control here so a review-only outcome
                    is never a dead end; without it the operator is stuck (the
                    full "Suggested Fix" section, and its Regenerate button, is
                    hidden whenever isReviewOnly is true). */}
                {!isReviewed && canGenerateSuggestion && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        // force=true — re-run the generator even though a stored
                        // (review-only) suggestion already exists.
                        issue && onGenerateSuggestion?.(issue.id, true)
                      }
                      disabled={isGeneratingSuggestion}
                      className="gap-1.5"
                    >
                      {isGeneratingSuggestion ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="h-3.5 w-3.5" />
                      )}
                      {isGeneratingSuggestion
                        ? "Regenerating..."
                        : "Regenerate fix"}
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      Re-run the resolver — if it can now resolve a safe fix, this
                      leaves review-only mode.
                    </span>
                  </div>
                )}
                {/* Needs a human: a review-only finding the tool can't safely
                    auto-apply (e.g. a redirect owned by another plugin, or
                    editorial work) shouldn't dead-end. Offer to file an
                    expert-fix ticket. Overridable findings (noindex/sitemap/
                    canonical) already have "Apply anyway", so they're excluded. */}
                {!isReviewed && !isManualRequested && !isOverridable && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {/* Deliberately LOW-emphasis (outline, not coral primary):
                        expert fix is an optional escalation, never the pushed
                        default — the operator keeps autonomy over automated
                        recommendations (orphan-pages feedback). */}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={!issue || requestManualFix.isPending}
                      onClick={() => issue && requestManualFix.mutate(issue.id)}
                      className="gap-1.5"
                    >
                      {requestManualFix.isPending ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <LifeBuoy className="h-3.5 w-3.5" />
                      )}
                      Request expert fix
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      Files a ticket so an AgileCo expert can fix this by hand.
                    </span>
                  </div>
                )}
                {isManualRequested && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-teal-dark">
                    <LifeBuoy className="h-4 w-4" />
                    Expert fix requested — the AgileCo team will handle this.
                  </div>
                )}
              </div>
              {renderDiffCard()}
            </section>
          )}

          {modalTab === "fix" && !isReviewOnly && (
            <section>
              {issue &&
                isAutoSuggestFailed(
                  issue as Parameters<typeof isAutoSuggestFailed>[0],
                ) && (
                <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/5 p-3">
                  <p className="flex items-start gap-2 text-sm text-destructive">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>
                      <span className="font-semibold">Fix generation failed.</span>{" "}
                      The background attempt didn&apos;t produce a suggestion —
                      use <span className="font-medium">Generate fix</span> below
                      to retry.
                    </span>
                  </p>
                  {/* The backend classifies every failure (EMPTY_RESPONSE / AUTH /
                      RATE_LIMIT / TIMEOUT …) and says who has to fix it. Show that
                      verbatim — a bare "failed" sent Jacob hunting API keys for a
                      provider-side outage (2026-07-08). */}
                  {(() => {
                    const err = (
                      issue.issue_details as {
                        auto_suggest?: { error?: string };
                      }
                    )?.auto_suggest?.error;
                    return err ? (
                      <p className="mt-2 select-text break-words rounded border border-destructive/20 bg-card/60 px-2 py-1.5 font-mono text-xs text-destructive">
                        {err}
                      </p>
                    ) : null;
                  })()}
                  {/* 3+ failed attempts: stop suggesting "retry" as the only
                      plan — offer the expert-fix ticket (the generation error
                      rides on the ticket automatically). */}
                  {Number(
                    (issue.issue_details as {
                      auto_suggest?: { attempts?: number };
                    })?.auto_suggest?.attempts ?? 0,
                  ) >= 3 && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        disabled={requestManualFix.isPending}
                        onClick={() => issue && requestManualFix.mutate(issue.id)}
                        className="gap-1.5 bg-coral text-white hover:bg-coral-hover"
                      >
                        {requestManualFix.isPending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <LifeBuoy className="h-3.5 w-3.5" />
                        )}
                        Request expert fix
                      </Button>
                      <span className="text-xs text-muted-foreground">
                        Several attempts failed — file a ticket so the AgileCo
                        team can handle it.
                      </span>
                    </div>
                  )}
                </div>
              )}
              <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Suggested Fix
                  </h3>
                  {issue && typeHelp && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {isInternalLinkIssue
                        ? "Generate or edit the link recommendation here, then apply the selected internal links to WordPress."
                        : isLowReadabilityIssue
                          ? hasSuggestedFix
                            ? "Review the generated sentence edits, then apply them directly."
                            : "Generate sentence edits first, then review and apply them."
                          : isThinContentIssue
                            ? thinContentApply &&
                              thinContentApply.isBlog &&
                              !thinContentApply.croRisk
                              ? hasSuggestedFix
                                ? "Review and edit the expansion brief, then apply it. Apply appends the new sections to the post — it never overwrites existing content."
                                : "Generate an expansion brief first, then review the draft sections before applying. Apply appends new sections — it never overwrites existing content."
                              : hasSuggestedFix
                                ? "Review and edit the expansion brief, then save the recommendation. There is no WordPress apply for this page."
                                : "Generate an expansion brief first, then review the draft sections before saving the recommendation."
                            : isMissingSchemaIssue
                              ? isMissingSchemaApplyable
                                ? "Review and edit the generated JSON-LD here, then apply the structured data to WordPress."
                                : "Review the generated JSON-LD here. This entity type has no safe automatic write, so it is handed off for manual implementation."
                              : isPageExcluded
                                ? "Generate or edit the suggested fix here. WordPress apply is disabled for page URLs."
                              : isAutoMode
                                ? isLargeImage
                                  ? "Review the image details, then optimize it with one confirmation."
                                  : "Generate or edit the suggested fix here, then apply it to WordPress."
                                : "Generate or edit the recommendation here, then save it."}
                    </p>
                  )}
                </div>
                {issue && canGenerateSuggestion && (
                  <div className="flex flex-wrap items-center gap-2">
                    {hasSuggestedFix && (
                      <ConfidenceBadge fix={issue.proposed_fix} />
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        // ALWAYS force. This used to pass `hasSuggestedFix`, so
                        // the request only forced when the drawer's copy of the
                        // issue already showed a suggestion — and the backend
                        // silently returns the CACHED packet when force is
                        // falsy (`if issue.proposed_value and not force: return
                        // issue`), answering 200 with the old value. The
                        // operator sees the button do nothing.
                        //
                        // There is no case where someone presses this and wants
                        // the cached answer: with no suggestion, force is a
                        // no-op; with one, forcing is the whole point. Deriving
                        // it from render state only created a way to get it
                        // wrong.
                        onGenerateSuggestion?.(issue.id, true)
                      }
                      disabled={isGeneratingSuggestion}
                      // Beat only when this is a first-time Generate; a
                      // Regenerate is a repeat action and shouldn't nag.
                      className={`${!isGeneratingSuggestion && !hasSuggestedFix ? "animate-ai-suggest" : ""} gap-1.5`}
                    >
                      {isGeneratingSuggestion ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : hasSuggestedFix ? (
                        <RefreshCw className="h-3.5 w-3.5" />
                      ) : (
                        <Sparkles className="h-3.5 w-3.5" />
                      )}
                      {isGeneratingSuggestion
                        ? "Generating..."
                        : hasSuggestedFix
                          ? isThinContentIssue
                            ? "Regenerate expansion"
                            : "Regenerate fix"
                          : isThinContentIssue
                            ? "Generate expansion"
                            : "Generate fix"}
                    </Button>
                  </div>
                )}
              </div>
              {isPageExcluded && (
                <div className="rounded-md border border-muted bg-muted/40 p-4 text-sm">
                  <p className="font-medium text-foreground">
                    Page — excluded from auto-fix
                  </p>
                  <p className="mt-1 text-muted-foreground">
                    Writing the SEO/meta title tag is limited to blog posts.
                    This URL is classified as a page, so you can still generate
                    and save a review-only suggestion here, but WordPress apply
                    stays disabled.
                  </p>
                </div>
              )}
              {renderDiffCard()}
            </section>
          )}

          {modalTab === "history" && issue && (
            <>
              <FixHistoryTimeline issueId={issue.id} />
              <section className="px-6 pb-6" data-testid="fix-history-panel">
                <FixHistoryPanel issueId={issue.id} />
              </section>
            </>
          )}
        </div>

        {(isApplied || isVerified) && !isReviewOnly && !isRecommended && (
          <div className="shrink-0 border-t border-border bg-teal-light/40 px-6 py-2.5">
            <p className="flex items-start gap-2 text-xs text-teal-dark">
              <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                Saved in WordPress. The live page can take a few minutes to
                reflect this change while the site&apos;s cache/CDN clears — the
                fix and its verification aren&apos;t affected by the delay.
              </span>
            </p>
          </div>
        )}
        {isRecommended && (
          <div className="shrink-0 border-t border-border bg-coral-light/50 px-6 py-2.5">
            <p className="flex items-start gap-2 text-xs text-coral-dark">
              <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                Recommendation saved. Nothing was changed on your site yet —
                this fix needs to be applied manually in WordPress.
              </span>
            </p>
          </div>
        )}
        <DialogFooter className="shrink-0 border-t border-border px-6 py-4">
          <div className="flex w-full flex-wrap items-center justify-end gap-2">
            {centralChat && isThinContentIssue && (
              <Button
                variant="ghost"
                onClick={handleChatWithAi}
                className="mr-auto gap-1.5 text-muted-foreground hover:text-foreground"
              >
                <MessageCircle className="h-4 w-4" />
                Chat with AI
              </Button>
            )}
            {/* Only where the issue has no expert route of its own — see
                showExpertHelp. Left-aligned and secondary so it never competes
                with Apply. */}
            {showExpertHelp && (
              <Button
                variant="ghost"
                onClick={() => setExpertHelpOpen(true)}
                disabled={!issue || requestExpertHelp.isPending}
                className="mr-auto gap-1.5 text-muted-foreground hover:text-foreground"
              >
                <LifeBuoy className="h-4 w-4" />
                Get expert help
              </Button>
            )}
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            {isReviewOnly ? (
              <>
                {wpEditUrl && (
                  <Button
                    variant="outline"
                    onClick={() => window.open(wpEditUrl, "_blank", "noopener")}
                    disabled={!issue}
                    className="gap-1.5"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Open in WordPress
                  </Button>
                )}
                {onVerify && (
                  <Button
                    variant="outline"
                    onClick={handleVerify}
                    disabled={!issue}
                    className="gap-1.5"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Re-check
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={handleReject}
                  disabled={!issue || isReviewed}
                  className="gap-1.5 border-teal/40 text-teal-dark hover:bg-teal-light"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  {isReviewed ? "Reviewed" : "Keep as-is"}
                </Button>
                {onApply && isOverridable && !isReviewed && (
                  <Button
                    onClick={handleApplyOverride}
                    disabled={!issue || isApplied || isApplying}
                    className="gap-1.5 bg-coral text-white hover:bg-coral-hover"
                  >
                    {isApplying ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Applying…
                      </>
                    ) : (
                      "Apply anyway"
                    )}
                  </Button>
                )}
              </>
            ) : isLargeImage ? (
              <>
                <Button
                  variant="outline"
                  onClick={handleReject}
                  disabled={
                    !issue || isTerminalReviewStatus || largeImageResultTerminal
                  }
                  className="border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                  Skip
                </Button>
                <Button
                  onClick={handleApply}
                  disabled={
                    !issue ||
                    !onApply ||
                    isTerminalReviewStatus ||
                    isVerified ||
                    largeImageResultTerminal
                  }
                  className="bg-teal text-white hover:bg-teal-hover"
                >
                  <ImageIcon className="mr-1.5 h-4 w-4" />
                  {largeImageActionLabel}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={handleReject}
                  disabled={!issue || isApplied || isTerminalReviewStatus}
                  className="border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                  Skip
                </Button>
                {(isDirectApply ||
                  isRecommendationPreviewIssue ||
                  isNoindexImportantIssue ||
                  isHreflangIssue ||
                  isSitemapMissingIssue) &&
                  !previewPermanentlyUnavailable &&
                  onPreview && (
                    <Button
                      variant="outline"
                      onClick={handlePreview}
                      // Recommendation-only types have no pre-generated suggestion
                      // gate — the preview fetch IS what produces the LLM single-meta
                      // recommendation, so don't block it on needsSuggestionBeforeApply.
                      // noindex_important is deterministic (noindex -> index), so it
                      // never needs a suggestion before previewing either.
                      // hreflang_error's staged tag-set rewrite is deterministic too,
                      // and its preview is what reveals per-reason auto-fixability.
                      disabled={
                        !issue ||
                        (!isRecommendationPreviewIssue &&
                          !isNoindexImportantIssue &&
                          !isHreflangIssue &&
                          !isSitemapMissingIssue &&
                          needsSuggestionBeforeApply)
                      }
                      className="border-teal/40 text-teal-dark hover:bg-teal/10"
                    >
                      {isRecommendationPreviewIssue
                        ? "Preview recommendation"
                        : "Preview before/after"}
                    </Button>
                  )}
                {isManualFailure && (
                  <Button
                    onClick={() =>
                      issue &&
                      requestManualFix.mutate(issue.id, {
                        onSuccess: () => onClose(),
                      })
                    }
                    disabled={!issue || requestManualFix.isPending}
                    className="gap-1.5 bg-coral text-white hover:bg-coral-hover"
                  >
                    {requestManualFix.isPending ? (
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    ) : (
                      <LifeBuoy className="mr-1.5 h-4 w-4" />
                    )}
                    Request expert fix
                  </Button>
                )}
                {onApply && thinContentOverridable && (
                  <Button
                    onClick={handleThinContentOverride}
                    disabled={isApplying}
                    data-testid="thin-content-apply-override"
                    className="bg-coral text-white hover:bg-coral-hover"
                  >
                    {isApplying ? (
                      <FixingLoader label="Applying expansion…" />
                    ) : (
                      "Apply anyway to WordPress"
                    )}
                  </Button>
                )}
                {onApply &&
                  // Apply can NEVER succeed for this row (refused/false-positive
                  // sitemap row, unpublished source page, page excluded from
                  // auto-fix) — don't render a button that only ever says no.
                  !applyPermanentlyUnavailable &&
                  // Auto-apply types (broken_external/internal, links_to_redirect,
                  // etc.) stay RETRYABLE after a failure — the failure may have
                  // been transient or a since-fixed bug, so keep the Apply button
                  // (labeled "Retry apply") alongside Request expert fix.
                  (!isManualFailure || (isFailed && isAutoMode)) &&
                  (isThinContentIssue
                    ? isThinContentBlogEligible
                    : isMissingSchemaIssue
                      ? isMissingSchemaApplyable
                      : isAutoMode || hasSuggestedFix) && (
                    <Button
                      onClick={handleApply}
                      data-testid={
                        isThinContentIssue
                          ? "thin-content-apply-expansion"
                          : isInternalLinkIssue
                            ? "internal-link-apply"
                            : undefined
                      }
                      disabled={
                        !issue ||
                        isApplied ||
                        // Expert-owned: a ticket is open (or was just resolved
                        // by hand). Applying from here would fight the expert's
                        // work on the same page.
                        isExpertOwned ||
                        isTerminalReviewStatus ||
                        isVerified ||
                        isExcludedFromApply ||
                        isApplying ||
                        needsSuggestionBeforeApply ||
                        needsSeoMetaEditValue ||
                        needsMultipleMetaValue ||
                        needsReadabilitySentenceRegeneration ||
                        needsReadabilitySentenceSelection ||
                        needsInternalLinkSourceSelection ||
                        thinContentApplyBlocked ||
                        sitemapApplyBlocked ||
                        sourceNotPublic
                      }
                      className="bg-teal text-white hover:bg-teal-hover"
                    >
                      {isApplying ? (
                        <FixingLoader
                          label={
                            isThinContentIssue
                              ? "Applying expansion…"
                              : isInternalLinkIssue
                                ? "Applying links…"
                                : "Applying fix…"
                          }
                        />
                      ) : isManualApplied ? (
                        "Fixed by expert — awaiting verification"
                      ) : isManualRequested ? (
                        "Being fixed by our experts"
                      ) : isThinContentIssue ? (
                        "Apply reviewed expansion"
                      ) : isLowReadabilityIssue ? (
                        "Apply reviewed edits"
                      ) : isInternalLinkIssue ? (
                        "Apply selected links"
                      ) : isMissingSchemaIssue ? (
                        "Apply to WordPress"
                      ) : isAutoMode ? (
                        isFailed ? "Retry apply" : "Apply to WordPress"
                      ) : (
                        "Save recommendation"
                      )}
                    </Button>
                  )}
                {onVerify && (isApplied || isFailed) && !isVerified && (
                  <Button
                    onClick={handleVerify}
                    className="bg-teal-dark text-white hover:bg-teal"
                  >
                    Verify
                  </Button>
                )}
                {/* Universal escape hatch (GH #614): any actionable issue the system
                    can't self-serve-fix — recommendation-only, no suggestion, a
                    `manual_required` refusal that stays pending, or a failure that
                    still has a self-serve recovery — offers an expert ticket instead
                    of dead-ending. Low emphasis so it never competes with Apply. */}
                {showExpertFallback && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => issue && requestManualFix.mutate(issue.id)}
                    disabled={!issue || requestManualFix.isPending}
                    className="gap-1.5 border-coral/40 text-coral-dark hover:bg-coral-light"
                  >
                    {requestManualFix.isPending ? (
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    ) : (
                      <LifeBuoy className="mr-1.5 h-4 w-4" />
                    )}
                    Request expert fix
                  </Button>
                )}
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>

      <ExpertHelpDialog
        open={expertHelpOpen}
        onOpenChange={setExpertHelpOpen}
        pageUrl={issue?.page_url}
        issueLabel={issue ? getIssueTypeLabel(issue) : undefined}
        onConfirm={async () => {
          if (!issue) return;
          await requestExpertHelp.mutateAsync({ issueId: issue.id });
        }}
      />
    </Dialog>
  );
}
